import { Component } from '@theme/component';
import { onDocumentLoaded, setHeaderMenuStyle, calculateHeaderGroupHeight } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer
 * @property {HTMLElement} headerMenu
 * @property {HTMLElement} headerRowTop
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * Cached header height to avoid repeated layout reads
   * @type {number}
   */
  #headerHeight = 0;

  /**
   * Keeps the global header height custom properties up to date for
   * sticky offsets and other dependent layout.
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
    this.#headerHeight = roundedHeaderHeight;
    document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);
    document.body.style.setProperty('--header-visible-height', `${roundedHeaderHeight}px`);

    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Handles the overflow minimum event from the header menu.
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer.
   * @param {boolean} hideMenu
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.#menuDrawerHiddenWidth = window.innerWidth;
    } else {
      this.#menuDrawerHiddenWidth = null;
    }

    setHeaderMenuStyle();
  }

  /**
   * In the cleaned-up sticky model the header does not animate between
   * scroll states anymore. We keep one stable visual state per page.
   */
  #applyStickyState = () => {
    const headerHeight = this.#headerHeight || this.offsetHeight || 0;

    this.dataset.stickyState = 'active';
    document.body.style.setProperty('--header-visible-height', `${headerHeight}px`);
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);
    this.#applyStickyState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.body.style.setProperty('--header-height', '0px');
    document.body.style.setProperty('--header-visible-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');
  const headerTopRow =
    header instanceof HTMLElement ? /** @type {HTMLElement | null} */ (header.querySelector('.header__row--top')) : null;

  if (headerGroup instanceof HTMLElement) {
    const syncHeaderGroupMetrics = () => {
      const headerGroupHeight = Math.round(calculateHeaderGroupHeight(header, headerGroup));
      document.body.style.setProperty('--header-group-height', `${headerGroupHeight}px`);

      if (header instanceof HTMLElement && headerTopRow) {
        window.requestAnimationFrame(() => {
          header.style.setProperty('--top-row-height', `${headerTopRow.offsetHeight}px`);
        });
      }
    };

    const resizeObserver = new ResizeObserver(() => {
      syncHeaderGroupMetrics();
    });

    syncHeaderGroupMetrics();

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type !== 'childList') continue;

        const updatedChildren = headerGroup.children;
        for (let i = 0; i < updatedChildren.length; i++) {
          const element = updatedChildren[i];
          if (element instanceof HTMLElement) {
            resizeObserver.observe(element);
          }
        }

        syncHeaderGroupMetrics();
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
